import { amountLabel } from "@web/features/terminal/presentation";
import type {
  AccountSnapshot,
  BrokerSnapshot,
  ProtocolSnapshot,
} from "@stonkland/protocol/api-types";
import type { RouteKey } from "@web/routes";

type AppPageHeaderProps = {
  account?: AccountSnapshot;
  broker?: BrokerSnapshot;
  protocol?: ProtocolSnapshot;
  route: RouteKey;
};

const copy: Partial<
  Record<RouteKey, { kicker: string; title: string; description: string }>
> = {
  portfolio: {
    kicker: "Portfolio",
    title: "Manage your Brokers",
    description:
      "Review Broker assets, work status and earning tiers from one place.",
  },
  trade: {
    kicker: "Trading",
    title: "Trade LAND and Brokers",
    description:
      "Swap ETH for LAND and trade Broker NFTs from one screen.",
  },
  rewards: {
    kicker: "Rewards",
    title: "Broker rewards",
    description:
      "See the assets inside your Broker and its estimated share of the next emission.",
  },
  loans: {
    kicker: "Broker-backed loans",
    title: "Borrow",
    description:
      "Lock a Broker, borrow tokens, and repay before the deadline to get it back.",
  },
};

export function AppPageHeader({
  account,
  broker,
  protocol,
  route,
}: AppPageHeaderProps) {
  const page = copy[route] || copy.portfolio!;
  const metrics = protocol?.metrics;
  const minDays = metrics
    ? Number(BigInt(metrics.loanMinDuration) / 86_400n)
    : undefined;
  const maxDays = metrics
    ? Number(BigInt(metrics.loanMaxDuration) / 86_400n)
    : undefined;

  const stats =
    route === "portfolio"
      ? [
          ["Owned Brokers", account?.brokerBalance || "—"],
          [
            "LAND balance",
            amountLabel(account ? BigInt(account.landBalance) : undefined, 2),
          ],
          [
            "Selected tier",
            broker
              ? broker.activation.tier
                ? `Tier ${broker.activation.tier}`
                : "Off shift"
              : "—",
          ],
        ]
      : route === "trade"
        ? [
            [
              "Exchange unit",
              metrics
                ? `${amountLabel(BigInt(metrics.ammPrice), 0)} LAND`
                : "—",
            ],
            [
              "Random fee",
              metrics
                ? `${amountLabel(BigInt(metrics.ammSwapFee), 5)} ETH`
                : "—",
            ],
            [
              "Exact-ID fee",
              metrics
                ? `${amountLabel(BigInt(metrics.ammSnipeFee), 5)} ETH`
                : "—",
            ],
            ["Inventory", metrics ? `${metrics.ammInventory} Brokers` : "—"],
            [
              "Minted",
              metrics ? `${metrics.totalSupply} / ${metrics.maxSupply}` : "—",
            ],
          ]
        : route === "rewards"
          ? [
              ["Owned Brokers", account?.brokerBalance || "—"],
              ["Selected Broker", broker ? `#${broker.tokenId.padStart(4, "0")}` : "—"],
              [
                "Work status",
                broker?.activation.tier
                  ? `Working · Tier ${broker.activation.tier}`
                  : broker
                    ? "Not working"
                    : "—",
              ],
            ]
          : [
              [
                "Principal",
                metrics
                  ? `${amountLabel(BigInt(metrics.loanPrincipal), 0)} LAND`
                  : "—",
              ],
              ["Rate", metrics ? `${metrics.loanApyBps / 100}% APY` : "—"],
              ["Term", minDays && maxDays ? `${minDays}–${maxDays} days` : "—"],
              [
                "Grace",
                metrics
                  ? `${Number(BigInt(metrics.loanGracePeriod) / 86_400n)} days`
                  : "—",
              ],
            ];

  return (
    <>
      <div className="app-page-heading">
        <div>
          <span className="section-kicker">{page.kicker}</span>
          <h1>{page.title}</h1>
          <p>{page.description}</p>
        </div>
      </div>
      <div className={`context-stats context-stats-${stats.length}`}>
        {stats.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
    </>
  );
}
